import React, { useCallback, useEffect, useState } from 'react';
import { X, History, Loader2, BookmarkPlus } from 'lucide-react';
import { ProposalBuilderVersionDoc, TimberNodeCostSnapshot } from '../../types';
import { formatProposalVersionSupplierRfqCell } from '../../utils/proposalRfqVersionLink';
import { formatProposalVersionTimestamp, formatProposalVersionSource, listProposalVersions } from '../../utils/proposalVersioning';
import {
  computeVersionSpeciesPricingRows,
  formatVersionSpeciesPricingSummary,
} from '../../utils/proposalVersionSpeciesPricing';
import './ProposalVersionHistoryModal.css';

function fmtMoney(n: number | undefined) {
  if (n === undefined || n === null || Number.isNaN(n)) return '—';
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

/** Summarize per-structure timber snapshots for the history table (materials vs billable kit). */
function fmtTimberStructures(rows: TimberNodeCostSnapshot[] | undefined): string {
  if (!rows?.length) return '—';
  const sumMat = rows.reduce((s, r) => s + r.timberMaterialsTotal, 0);
  const sumBill = rows.reduce((s, r) => s + (r.billableTimberKit ?? 0), 0);
  const hasBill = rows.some(r => typeof r.billableTimberKit === 'number');
  const detail = rows
    .map(r => {
      const bill = typeof r.billableTimberKit === 'number' ? ` · billable ${fmtMoney(r.billableTimberKit)}` : '';
      return `${r.name}: ${fmtMoney(r.timberMaterialsTotal)} mats${bill}`;
    })
    .join(' · ');
  if (detail.length <= 120) return detail;
  const billPart = hasBill ? ` · billable timber ${fmtMoney(sumBill)}` : '';
  return `${rows.length} structures · mats ${fmtMoney(sumMat)}${billPart}`;
}

function fmtSpeciesPricingList(snapshot: ProposalBuilderVersionDoc['snapshot']) {
  if (!snapshot) return { summary: '—', rows: [] as ReturnType<typeof computeVersionSpeciesPricingRows> };
  const rows = computeVersionSpeciesPricingRows(snapshot);
  return { summary: formatVersionSpeciesPricingSummary(rows), rows };
}

export interface ProposalVersionHistoryModalProps {
  open: boolean;
  onClose: () => void;
  dealId: string;
  /** Persist current proposal (save) then write a version row. */
  onSaveCurrentVersion: (label: string) => Promise<void>;
  onOpenSnapshot: (version: ProposalBuilderVersionDoc) => void;
}

const ProposalVersionHistoryModal: React.FC<ProposalVersionHistoryModalProps> = ({
  open,
  onClose,
  dealId,
  onSaveCurrentVersion,
  onOpenSnapshot,
}) => {
  const [versions, setVersions] = useState<ProposalBuilderVersionDoc[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [label, setLabel] = useState('');
  const [saving, setSaving] = useState(false);

  const refresh = useCallback(async () => {
    if (!dealId) return;
    setLoading(true);
    setLoadError(null);
    try {
      const list = await listProposalVersions(dealId);
      setVersions(list);
    } catch (e) {
      console.error(e);
      setLoadError('Could not load version history.');
      setVersions([]);
    } finally {
      setLoading(false);
    }
  }, [dealId]);

  useEffect(() => {
    if (open && dealId) {
      void refresh();
    }
  }, [open, dealId, refresh]);

  const handleSaveVersion = async () => {
    setSaving(true);
    try {
      await onSaveCurrentVersion(label.trim());
      setLabel('');
      await refresh();
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="pvh-overlay" onClick={onClose}>
      <div className="pvh-modal" onClick={e => e.stopPropagation()}>
        <div className="pvh-header">
          <div className="pvh-title-row">
            <History size={22} aria-hidden />
            <h2>Proposal version history</h2>
          </div>
          <button type="button" className="pvh-close" onClick={onClose} aria-label="Close">
            <X size={20} />
          </button>
        </div>

        <div className="pvh-body">
          <p className="pvh-intro">
            Save a named checkpoint of the proposal as it exists in the builder (unsaved edits are saved first). Open any row to view a read-only snapshot.
          </p>

          <div className="pvh-save-row">
            <input
              type="text"
              className="pvh-label-input"
              placeholder="Optional label (e.g. Before client call)"
              value={label}
              onChange={e => setLabel(e.target.value)}
              disabled={saving}
            />
            <button
              type="button"
              className="pvh-save-btn"
              onClick={() => void handleSaveVersion()}
              disabled={saving}
            >
              {saving ? <Loader2 size={16} className="pvh-spin" /> : <BookmarkPlus size={16} />}
              Save current as version
            </button>
          </div>

          {loadError && <p className="pvh-error">{loadError}</p>}

          <div className="pvh-table-wrap">
            {loading ? (
              <div className="pvh-loading">
                <Loader2 size={24} className="pvh-spin" />
                <span>Loading versions…</span>
              </div>
            ) : versions.length === 0 ? (
              <p className="pvh-empty">No saved versions yet.</p>
            ) : (
              <table className="pvh-table">
                <thead>
                  <tr>
                    <th>When</th>
                    <th>Source</th>
                    <th>Label</th>
                    <th>Supplier RFQ</th>
                    <th className="pvh-num">Species pricing</th>
                    <th>Timber by structure</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {versions.map(v => {
                    const timberSummary = fmtTimberStructures(v.timberNodeSnapshots);
                    const { summary: speciesSummary, rows: speciesRows } = fmtSpeciesPricingList(v.snapshot);
                    return (
                    <tr key={v.id}>
                      <td>{formatProposalVersionTimestamp(v)}</td>
                      <td>
                        <span className={`pvh-badge pvh-badge--${v.source}`}>
                          {formatProposalVersionSource(v.source)}
                        </span>
                      </td>
                      <td>{v.label || '—'}</td>
                      <td>{formatProposalVersionSupplierRfqCell(v)}</td>
                      <td className="pvh-species-col" title={speciesSummary}>
                        <ul className="pvh-species-list">
                          {speciesRows.length === 0 ? (
                            <li className="pvh-species-row">—</li>
                          ) : (
                            speciesRows.map((row) => (
                              <li
                                key={row.speciesName}
                                className={`pvh-species-row${row.isActive ? ' pvh-species-active' : ''}`}
                              >
                                <span className="pvh-species-name">{row.speciesName}</span>
                                <span className="pvh-species-price">{fmtMoney(row.billableTotal)}</span>
                                {row.isActive && <span className="pvh-species-badge">Active</span>}
                              </li>
                            ))
                          )}
                        </ul>
                      </td>
                      <td className="pvh-timber-col" title={timberSummary}>
                        {timberSummary}
                      </td>
                      <td className="pvh-actions">
                        <button
                          type="button"
                          className="pvh-view-btn"
                          onClick={() => onOpenSnapshot(v)}
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProposalVersionHistoryModal;
